/**
 * EN / ES translations for the /spreadsheet-rescue service page.
 * Imported and merged into the main translations object.
 */
export const spreadsheetRescueTranslations = {
  en: {
    spreadsheetRescue: {
      heroEyebrow: "Kranbos Spreadsheet Rescue",
      heroTitle: "Turn the Spreadsheet Your Business Depends On Into Something You Can Trust",
      heroSubtitle:
        "Kranbos repairs, simplifies, and improves business-critical spreadsheets—then helps automate or replace them when the process has outgrown the workbook.",
      heroPrimaryCta: "Request a Spreadsheet Review",
      heroSecondaryCta: "See What We Fix",

      visualLabel: "Illustrative example: the same job tracker before and after a rescue",
      visualDisclaimer:
        "Sample data shown for illustration only. It does not represent a real client workbook.",
      beforeLabel: "Before",
      afterLabel: "After",
      colCustomer: "Customer",
      colDate: "Date",
      colStatus: "Status",
      colTotal: "Total",
      colJob: "Job",
      colOwner: "Assigned to",
      beforeCustomer1: "Customer A",
      beforeCustomer2: "customer a",
      beforeCustomer3: "Customer B",
      beforeCustomer4: "Customer C",
      beforeStatus1: "open",
      beforeStatus2: "Open ",
      beforeStatus3: "OPEN?",
      beforeStatus4: "overdue",
      beforeMissing: "(blank)",
      beforeFormula: "=SUM(D2:D14)",
      beforeFormulaWarning: "Warning: rows added below row 14 are left out of this total",
      beforeNote1: "Error: #REF! in the total column",
      beforeNote2: "Duplicate customer rows",
      beforeNote3: "Dates entered three different ways",
      beforeNote4: "Overdue work marked by hand",
      beforeColumnNote: "Note in the header: “Do not edit this column”",
      beforeTab1: "Sheet1",
      beforeTab2: "Sheet1 (2)",
      beforeTab3: "final_v3",
      afterFormula: "=SUM(Jobs[Total])",
      afterFormulaNote: "Structured range, protected from accidental edits",
      afterStatusScheduled: "Scheduled",
      afterStatusEstimate: "Awaiting estimate",
      afterStatusOverdue: "Overdue",
      afterStatusInvoiced: "Invoiced",
      afterOwner1: "Crew 1",
      afterOwner2: "Crew 2",
      afterOwner3: "Crew 3",
      afterValidated: "Validated dropdown",
      afterSummaryLabel: "Summary",
      afterSummary1: "Open jobs: 12",
      afterSummary2: "Overdue: 3",
      afterSummary3: "Awaiting estimate: 5",
      afterAttentionLabel: "Needs attention",
      afterAttention1: "2 jobs without an assigned crew",
      afterAttention2: "1 estimate open longer than 14 days",
      afterTab1: "Jobs",
      afterTab2: "Estimates",
      afterTab3: "Summary",

      problemEyebrow: "When a workbook becomes a system",
      problemTitle: "It started as a spreadsheet. Now it runs part of the business.",
      problemText1:
        "Spreadsheets often begin as a quick and practical way to track information.",
      problemText2:
        "Over time, more employees use them, more columns are added, formulas become harder to follow, separate versions are created, and important decisions begin to depend on information that is increasingly difficult to verify.",
      problemText3:
        "Eventually, the spreadsheet becomes an unofficial business system—but without the structure, controls, documentation, or reliability of one.",
      problemText4:
        "Kranbos helps businesses understand what the spreadsheet is doing, fix what is broken, and create a more dependable way to manage the process.",
      warningSignsLabel: "Warning signs",
      warning1: "Nobody knows how all the formulas work",
      warning2: "One employee is the only person who can maintain it",
      warning3: "Different versions contain different numbers",
      warning4: "Employees copy and paste information between tabs or files",
      warning5: "Rows are accidentally overwritten or deleted",
      warning6: "Reports require hours of manual cleanup",
      warning7: "Important fields are entered inconsistently",
      warning8: "The workbook becomes slower every month",
      warning9: "People avoid changing it because they are afraid it will break",
      warning10: "Business decisions depend on numbers that are difficult to verify",

      capabilitiesEyebrow: "What we can fix",
      capabilitiesTitle: "Repair the workbook. Improve the process behind it.",
      capabilitiesIntro:
        "Kranbos looks beyond individual formulas. We evaluate how the spreadsheet is used, where the information comes from, who depends on it, and what happens before and after each update.",
      cap1Title: "Formula and workbook repair",
      cap1Text:
        "Fix broken formulas, incorrect references, inconsistent calculations, damaged links, and workbook logic that no longer produces reliable results.",
      cap2Title: "Data cleanup and organization",
      cap2Text:
        "Standardize dates, names, categories, statuses, identifiers, and other information so records are easier to search, compare, and report on.",
      cap3Title: "Workbook simplification",
      cap3Text:
        "Reduce unnecessary tabs, duplicate calculations, hidden dependencies, manual steps, and confusing layouts that make the file difficult to maintain.",
      cap4Title: "Safer data entry",
      cap4Text:
        "Add structured tables, dropdowns, validation rules, required fields, instructions, and protected areas to reduce avoidable errors.",
      cap5Title: "Reporting and summaries",
      cap5Text:
        "Create clearer summary views, filters, status reports, attention lists, and recurring reports based on the information inside the workbook.",
      cap6Title: "Automation and migration",
      cap6Text:
        "Automate repetitive spreadsheet tasks or move the process into a database, internal tool, or connected workflow when the workbook has reached its practical limit.",

      situationsEyebrow: "Common rescue situations",
      situationsTitle: "The issue is often bigger than one broken formula",
      situation1Title: "Multiple versions of the same file",
      situation1Text:
        "Employees save copies locally, send files by email, or create separate versions that no longer contain the same information.",
      situation2Title: "Fragile formulas",
      situation2Text:
        "Calculations depend on fixed cell ranges, copied formulas, hidden tabs, or references that break when someone inserts a row or changes a file name.",
      situation3Title: "Inconsistent data entry",
      situation3Text:
        "The same customer, status, service, date, or location is entered in several different ways, making filtering and reporting unreliable.",
      situation4Title: "Manual consolidation",
      situation4Text:
        "Someone repeatedly copies information from multiple spreadsheets, systems, exports, or departments into one master workbook.",
      situation5Title: "Unclear ownership",
      situation5Text:
        "Employees do not know who should update each field, which version is current, or when the information was last reviewed.",
      situation6Title: "Slow and oversized workbooks",
      situation6Text:
        "Large formulas, repeated formatting, excessive tabs, external links, or unnecessary data make the workbook difficult to open and use.",
      situation7Title: "Hidden business rules",
      situation7Text:
        "Important pricing, approval, scheduling, or classification rules exist only inside formulas that few people understand.",
      situation8Title: "Manual reporting",
      situation8Text:
        "Weekly or monthly reporting requires filtering, copying, formatting, and recalculating the same information again and again.",
      situation9Title: "Limited access control",
      situation9Text:
        "Anyone with access to the workbook may be able to view or change information beyond what they need for their role.",
      situation10Title: "Process dependency",
      situation10Text:
        "An important workflow stops when the one person who understands the spreadsheet is unavailable.",
      situationsNote:
        "A spreadsheet rescue should address the operational risk behind these problems—not simply make the workbook look cleaner.",

      exampleEyebrow: "Example rescue",
      exampleTitle: "From a fragile job tracker to a more dependable operations workbook",
      exampleIntro1:
        "A growing service company may use one workbook to track customer requests, estimates, scheduled work, assigned employees, contractors, job status, expenses, invoices, and follow-ups.",
      exampleIntro2:
        "The workbook may have started with a few columns, but over time it becomes the central place employees use to understand what is happening. As the business grows, the file may become difficult to maintain and increasingly risky to depend on.",
      exampleDisclaimer:
        "This is an illustrative example, not a real client case study.",
      exampleBeforeLabel: "Before the rescue",
      exampleAfterLabel: "After the rescue",
      before1Title: "Requests and estimates",
      before1Text:
        "New information is copied manually from email, forms, or another platform into the workbook.",
      before2Title: "Inconsistent records",
      before2Text:
        "Customer names, service types, dates, and statuses are entered differently by each employee.",
      before3Title: "Assignment tracking",
      before3Text:
        "Employees manually highlight rows or add notes to show who is responsible for the work.",
      before4Title: "Broken reporting",
      before4Text: "Summary formulas use fixed ranges and fail to include newly added rows.",
      before5Title: "Multiple versions",
      before5Text:
        "Managers and employees keep separate copies because they are afraid of overwriting each other’s changes.",
      before6Title: "Weekly cleanup",
      before6Text:
        "Someone spends several hours correcting records and rebuilding the management report.",
      after1Title: "Structured records",
      after1Text:
        "The workbook uses organized tables with consistent columns, identifiers, and required information.",
      after2Title: "Validated data entry",
      after2Text:
        "Dropdowns, date controls, status options, and instructions reduce inconsistent entries.",
      after3Title: "Clear ownership",
      after3Text: "Assignments, due dates, and next steps follow a consistent structure.",
      after4Title: "Reliable summaries",
      after4Text: "Reports and attention views use structured ranges and documented calculations.",
      after5Title: "Safer collaboration",
      after5Text:
        "The file has clearer ownership, protected calculations, and a defined process for updates.",
      after6Title: "Reduced manual reporting",
      after6Text:
        "Recurring summaries and attention lists update from the structured data with less manual preparation.",
      exampleFlowLabel: "The rescued process",
      exampleFlow1: "Request received",
      exampleFlow2: "Record created",
      exampleFlow3: "Estimate tracked",
      exampleFlow4: "Work assigned",
      exampleFlow5: "Status updated",
      exampleFlow6: "Follow-up identified",
      exampleFlow7: "Weekly summary generated",
      exampleClosing:
        "The appropriate solution may be an improved workbook, an automated spreadsheet process, or a gradual move into a more structured internal system.",

      levelEyebrow: "The right level of rescue",
      levelTitle: "Some spreadsheets need repairs. Others have outgrown the format.",
      levelText1: "Kranbos evaluates the process before recommending a solution.",
      levelText2:
        "A well-designed spreadsheet can remain an effective tool for many businesses. However, a workbook may no longer be the best choice when too many people depend on it, the workflow requires complex permissions, or the process needs reliable integration with several systems.",
      levelWhenLabel: "Appropriate when",
      levelImprovementsLabel: "Possible improvements",
      levelReplacementLabel: "Possible replacement",
      level1Title: "Repair the spreadsheet",
      level1When1: "The workbook solves the correct problem but contains errors",
      level1When2: "The number of users is manageable",
      level1When3: "The workflow is relatively straightforward",
      level1When4: "Better formulas, structure, and validation can improve reliability",
      level1When5: "The spreadsheet remains the most practical format",
      level1Item1: "Formula correction",
      level1Item2: "Data cleanup",
      level1Item3: "Structured tables",
      level1Item4: "Clearer layout",
      level1Item5: "Protected calculations",
      level1Item6: "Validation and dropdowns",
      level1Item7: "Documentation",
      level2Title: "Automate the spreadsheet",
      level2When1: "The workbook is still useful but requires repetitive manual updates",
      level2When2: "Data must be imported or exported regularly",
      level2When3: "Reports are rebuilt on a recurring schedule",
      level2When4: "Employees repeatedly copy information between systems",
      level2When5: "Notifications or follow-ups depend on spreadsheet status",
      level2Item1: "Automated imports",
      level2Item2: "Scheduled exports",
      level2Item3: "Report generation",
      level2Item4: "Status notifications",
      level2Item5: "Record synchronization",
      level2Item6: "Document generation",
      level2Item7: "Workflow triggers",
      level3Title: "Replace the spreadsheet",
      level3When1: "Many users need simultaneous access",
      level3When2: "The workflow requires different roles and permissions",
      level3When3: "The spreadsheet contains too much business logic",
      level3When4: "Records need stronger history and auditability",
      level3When5: "Employees need forms, approvals, assignments, or work queues",
      level3When6: "The workbook has become too large or fragile",
      level3When7: "The process needs dependable system integrations",
      level3Item1: "Internal operations tool",
      level3Item2: "Database-backed application",
      level3Item3: "Controlled intake portal",
      level3Item4: "Work queue",
      level3Item5: "Administrative interface",
      level3Item6: "Custom reporting system",
      levelClosing:
        "Kranbos recommends the smallest practical solution that improves reliability without creating unnecessary complexity.",

      cleanupEyebrow: "Cleaner information",
      cleanupTitle: "Reliable spreadsheets depend on consistent data",
      cleanupText1:
        "A workbook can have perfect formulas and still produce misleading results if the underlying information is inconsistent.",
      cleanupText2:
        "Kranbos can help standardize the fields, categories, statuses, identifiers, and rules used throughout the spreadsheet process.",
      cleanup1Title: "Consistent categories",
      cleanup1Text:
        "Standardize services, departments, locations, customer types, statuses, and other recurring values.",
      cleanup2Title: "Duplicate detection",
      cleanup2Text:
        "Identify repeated customers, projects, invoices, requests, or other records that may represent the same item.",
      cleanup3Title: "Required information",
      cleanup3Text:
        "Clarify which fields must be completed before a record can move forward or appear in a report.",
      cleanup4Title: "Documented definitions",
      cleanup4Text:
        "Explain what important labels, statuses, totals, and calculations mean so different employees use them consistently.",
      cleanupNote:
        "Data cleanup should be reviewed with someone who understands the business. Kranbos does not automatically delete or merge uncertain records without an approved rule or human review.",

      collabEyebrow: "Safer collaboration",
      collabTitle: "Make it easier to update the file without accidentally breaking it",
      collabText1:
        "Many spreadsheet problems happen because data entry, calculations, reporting, and configuration all exist in the same editable area.",
      collabText2:
        "Kranbos can restructure the workbook so employees have clearer places to enter information while important formulas and business rules remain protected.",
      collab1Title: "Protected calculations",
      collab1Text:
        "Reduce accidental changes to formulas, summaries, reference tables, and other important workbook logic.",
      collab2Title: "Structured input areas",
      collab2Text:
        "Separate data entry from calculations and reporting so employees know where changes should be made.",
      collab3Title: "Clear ownership",
      collab3Text: "Define who is responsible for updates, review, corrections, and report preparation.",
      collab4Title: "Change documentation",
      collab4Text:
        "Document important formulas, fields, tabs, assumptions, and maintenance steps so the workbook is less dependent on one employee.",
      collabDisclaimer:
        "Spreadsheet protections can reduce accidental changes but should not be treated as a replacement for appropriate platform permissions, backups, security controls, or formal compliance requirements.",

      autoEyebrow: "Less copying and pasting",
      autoTitle: "Keep the spreadsheet where it works. Automate the steps around it.",
      autoText1:
        "Some businesses do not need to replace their spreadsheets. They need to stop manually moving information into and out of them.",
      autoText2:
        "Kranbos can evaluate opportunities to connect the workbook to forms, exports, approved APIs, email processes, documents, reporting tools, and other business systems.",
      auto1Title: "Import new records",
      auto1Text:
        "Bring approved information from forms, platforms, exports, or other structured sources into the workbook.",
      auto2Title: "Generate recurring reports",
      auto2Text:
        "Prepare weekly or monthly summaries without rebuilding the same report manually.",
      auto3Title: "Create documents",
      auto3Text:
        "Use approved spreadsheet data to populate estimates, summaries, letters, or other repeatable documents.",
      auto4Title: "Send notifications",
      auto4Text:
        "Alert the appropriate person when records are overdue, incomplete, unassigned, or ready for review.",
      auto5Title: "Update connected systems",
      auto5Text:
        "Send approved spreadsheet information to another platform when a reliable integration is available.",
      auto6Title: "Archive completed records",
      auto6Text:
        "Move older or completed information into an organized archive without losing important history.",
      autoNote:
        "Automation options depend on the spreadsheet platform, the source systems, available access, and the reliability of the underlying data.",
      autoCta: "Explore Workflow Automation",

      reportingEyebrow: "From workbook to visibility",
      reportingTitle: "Turn the spreadsheet into a clearer operational view",
      reportingText1:
        "Many businesses collect useful information in spreadsheets but struggle to turn it into reports that are easy to understand and maintain.",
      reportingText2:
        "Kranbos can help organize the data behind summaries, status views, attention lists, and management reports.",
      reportingCol1Title: "Spreadsheet improvement",
      reportingCol1Item1: "Clean and organize source records",
      reportingCol1Item2: "Standardize statuses and categories",
      reportingCol1Item3: "Repair calculations",
      reportingCol1Item4: "Reduce duplicate data",
      reportingCol1Item5: "Create structured tables",
      reportingCol1Item6: "Improve filtering and validation",
      reportingCol2Title: "Dashboards & Reporting",
      reportingCol2Item1: "Present high-level summaries",
      reportingCol2Item2: "Track activity and workload",
      reportingCol2Item3: "Highlight overdue or incomplete work",
      reportingCol2Item4: "Compare teams, locations, or time periods",
      reportingCol2Item5: "Deliver recurring management reports",
      reportingCol2Item6: "Provide clearer decision support",
      reportingClosing:
        "In some cases, the improved spreadsheet can power the report. In others, the reporting should move into a separate dashboard or internal tool.",
      reportingCta: "Explore Dashboards & Reporting",

      toolsEyebrow: "Beyond the workbook",
      toolsTitle: "A spreadsheet should not have to behave like a full business application",
      toolsText1:
        "A workbook may become difficult to manage when the process requires many users, different permission levels, forms, assignments, approvals, comments, activity history, or integration with several platforms.",
      toolsText2:
        "At that point, a lightweight internal tool may provide a clearer and safer operational experience.",
      toolsCol1Title: "A spreadsheet may remain appropriate when",
      toolsCol1Item1: "The user group is small",
      toolsCol1Item2: "The process is relatively simple",
      toolsCol1Item3: "Records do not require complex permissions",
      toolsCol1Item4: "A workbook is easy for the team to understand",
      toolsCol1Item5: "Collaboration needs are limited",
      toolsCol1Item6: "The spreadsheet can be maintained reliably",
      toolsCol2Title: "An internal tool may be more appropriate when",
      toolsCol2Item1: "Many employees or contractors need access",
      toolsCol2Item2: "Different users need different permissions",
      toolsCol2Item3: "The team needs one shared work queue",
      toolsCol2Item4: "Records require assignments and approvals",
      toolsCol2Item5: "Activity history is important",
      toolsCol2Item6: "Employees need structured forms",
      toolsCol2Item7: "The workflow connects several systems",
      toolsCol2Item8: "The spreadsheet has become difficult to scale safely",
      toolsClosing:
        "Kranbos can improve the current workbook first and create a migration path when replacing it immediately would be unnecessarily disruptive.",
      toolsCta: "Explore Internal Tools",

      processEyebrow: "How it works",
      processTitle: "Understand the workbook before changing it",
      process1Title: "Workbook and workflow review",
      process1Text:
        "We review the spreadsheet, its users, source information, reports, formulas, manual steps, and the business process it supports.",
      process2Title: "Risk and problem identification",
      process2Text:
        "We identify broken logic, inconsistent data, duplicate work, unclear ownership, performance issues, and areas where the process depends too heavily on manual effort.",
      process3Title: "Rescue recommendation",
      process3Text:
        "We recommend whether to repair, restructure, automate, connect, or gradually replace the spreadsheet.",
      process4Title: "Implementation and validation",
      process4Text:
        "We make the approved improvements, test calculations and workflows, and review uncertain business rules with the appropriate people.",
      process5Title: "Documentation and handoff",
      process5Text:
        "We document the workbook structure, important formulas, data-entry expectations, maintenance steps, and any new automation or reporting processes.",
      processNote:
        "The amount of work depends on the workbook size, complexity, available documentation, data quality, and number of connected files or systems.",

      needEyebrow: "Before the rescue",
      needTitle: "The workbook is only part of the story",
      needText1:
        "To improve a spreadsheet responsibly, Kranbos needs to understand how the business uses it and which information can be trusted.",
      need1: "The current workbook and related files",
      need2: "A description of what the spreadsheet is used for",
      need3: "The people who enter or review information",
      need4: "Known errors or unreliable calculations",
      need5: "Examples of reports created from the workbook",
      need6: "Source files, exports, or systems that provide the data",
      need7: "Definitions for important fields, statuses, and totals",
      need8: "Examples of the current manual process",
      need9: "Any previous or alternate workbook versions",
      need10: "The expected result or decision the workbook should support",
      needNote:
        "Sensitive or production data should be shared through an approved method. Where practical, the initial review can use a sanitized copy that preserves the workbook structure without exposing unnecessary confidential information.",

      outcomesEyebrow: "The result",
      outcomesTitle: "A spreadsheet your team can understand, maintain, and rely on",
      outcome1Title: "More reliable calculations",
      outcome1Text:
        "Reduce broken formulas, unclear references, inconsistent logic, and reporting errors.",
      outcome2Title: "Cleaner information",
      outcome2Text: "Make records easier to enter, filter, compare, review, and report on.",
      outcome3Title: "Less manual work",
      outcome3Text:
        "Reduce repetitive copying, formatting, consolidation, and report preparation.",
      outcome4Title: "A clearer path forward",
      outcome4Text:
        "Understand whether the process should remain in a spreadsheet, be automated, or move into a more structured tool.",

      relatedTitle: "The spreadsheet may be one part of a larger operational problem",
      relatedIntro:
        "Kranbos can combine spreadsheet improvements, automation, internal tools, and reporting into one practical solution.",
      relatedCurrentLabel: "You are here",
      related1Title: "Spreadsheet Rescue",
      related1Text:
        "Repair, simplify, automate, or replace a spreadsheet-based business process.",
      related2Title: "Workflow Automation",
      related2Text:
        "Connect systems and reduce repetitive movement of information and actions.",
      related3Title: "Internal Tools",
      related3Text:
        "Replace an overloaded spreadsheet with a focused operational interface when necessary.",
      related4Title: "Dashboards & Reporting",
      related4Text:
        "Turn organized business information into clearer reports, summaries, and attention views.",
      relatedLearnMore: "Learn more",

      finalCtaTitle: "Show us the spreadsheet everyone is afraid to touch",
      finalCtaText:
        "Tell us what the workbook does, where it is causing problems, and what your team needs from it. We will help determine whether it should be repaired, automated, or replaced.",
      finalPrimaryCta: "Request a Spreadsheet Review",
      finalSecondaryCta: "Contact Us",
    },
  },
  es: {
    spreadsheetRescue: {
      heroEyebrow: "Rescate de Hojas de Cálculo Kranbos",
      heroTitle:
        "Convierte la Hoja de Cálculo de la Que Depende Tu Negocio en Algo Confiable",
      heroSubtitle:
        "Kranbos repara, simplifica y mejora las hojas de cálculo críticas para tu negocio—y luego ayuda a automatizarlas o reemplazarlas cuando el proceso ya superó al archivo.",
      heroPrimaryCta: "Solicitar una Revisión de Hoja de Cálculo",
      heroSecondaryCta: "Ver Qué Arreglamos",

      visualLabel:
        "Ejemplo ilustrativo: el mismo control de trabajos antes y después del rescate",
      visualDisclaimer:
        "Datos de ejemplo mostrados solo con fines ilustrativos. No representan el archivo de un cliente real.",
      beforeLabel: "Antes",
      afterLabel: "Después",
      colCustomer: "Cliente",
      colDate: "Fecha",
      colStatus: "Estado",
      colTotal: "Total",
      colJob: "Trabajo",
      colOwner: "Asignado a",
      beforeCustomer1: "Cliente A",
      beforeCustomer2: "cliente a",
      beforeCustomer3: "Cliente B",
      beforeCustomer4: "Cliente C",
      beforeStatus1: "abierto",
      beforeStatus2: "Abierto ",
      beforeStatus3: "ABIERTO?",
      beforeStatus4: "vencido",
      beforeMissing: "(vacío)",
      beforeFormula: "=SUMA(D2:D14)",
      beforeFormulaWarning:
        "Advertencia: las filas agregadas debajo de la fila 14 quedan fuera de este total",
      beforeNote1: "Error: #¡REF! en la columna de total",
      beforeNote2: "Filas de clientes duplicadas",
      beforeNote3: "Fechas escritas de tres formas distintas",
      beforeNote4: "Trabajos vencidos marcados a mano",
      beforeColumnNote: "Nota en el encabezado: “No editar esta columna”",
      beforeTab1: "Hoja1",
      beforeTab2: "Hoja1 (2)",
      beforeTab3: "final_v3",
      afterFormula: "=SUMA(Trabajos[Total])",
      afterFormulaNote: "Rango estructurado y protegido contra cambios accidentales",
      afterStatusScheduled: "Programado",
      afterStatusEstimate: "Esperando presupuesto",
      afterStatusOverdue: "Vencido",
      afterStatusInvoiced: "Facturado",
      afterOwner1: "Equipo 1",
      afterOwner2: "Equipo 2",
      afterOwner3: "Equipo 3",
      afterValidated: "Lista desplegable validada",
      afterSummaryLabel: "Resumen",
      afterSummary1: "Trabajos abiertos: 12",
      afterSummary2: "Vencidos: 3",
      afterSummary3: "Esperando presupuesto: 5",
      afterAttentionLabel: "Requiere atención",
      afterAttention1: "2 trabajos sin equipo asignado",
      afterAttention2: "1 presupuesto abierto por más de 14 días",
      afterTab1: "Trabajos",
      afterTab2: "Presupuestos",
      afterTab3: "Resumen",

      problemEyebrow: "Cuando un archivo se convierte en un sistema",
      problemTitle: "Empezó como una hoja de cálculo. Hoy opera parte del negocio.",
      problemText1:
        "Las hojas de cálculo suelen empezar como una forma rápida y práctica de registrar información.",
      problemText2:
        "Con el tiempo, más empleados las usan, se agregan más columnas, las fórmulas son más difíciles de seguir, se crean versiones separadas y decisiones importantes empiezan a depender de información cada vez más difícil de verificar.",
      problemText3:
        "Con el tiempo, la hoja de cálculo se convierte en un sistema no oficial del negocio—pero sin la estructura, los controles, la documentación ni la confiabilidad de uno.",
      problemText4:
        "Kranbos ayuda a las empresas a entender qué hace la hoja de cálculo, corregir lo que está roto y crear una forma más confiable de manejar el proceso.",
      warningSignsLabel: "Señales de alerta",
      warning1: "Nadie sabe cómo funcionan todas las fórmulas",
      warning2: "Un solo empleado es el único que puede mantenerla",
      warning3: "Distintas versiones muestran números distintos",
      warning4: "Los empleados copian y pegan información entre pestañas o archivos",
      warning5: "Se sobrescriben o eliminan filas por accidente",
      warning6: "Los reportes requieren horas de limpieza manual",
      warning7: "Campos importantes se cargan de forma inconsistente",
      warning8: "El archivo se vuelve más lento cada mes",
      warning9: "Nadie quiere modificarla por miedo a romperla",
      warning10: "Las decisiones del negocio dependen de números difíciles de verificar",

      capabilitiesEyebrow: "Qué podemos arreglar",
      capabilitiesTitle: "Reparar el archivo. Mejorar el proceso detrás de él.",
      capabilitiesIntro:
        "Kranbos mira más allá de las fórmulas individuales. Evaluamos cómo se usa la hoja de cálculo, de dónde viene la información, quién depende de ella y qué ocurre antes y después de cada actualización.",
      cap1Title: "Reparación de fórmulas y del archivo",
      cap1Text:
        "Corregimos fórmulas rotas, referencias incorrectas, cálculos inconsistentes, vínculos dañados y lógica que ya no produce resultados confiables.",
      cap2Title: "Limpieza y organización de datos",
      cap2Text:
        "Estandarizamos fechas, nombres, categorías, estados, identificadores y demás información para que los registros sean más fáciles de buscar, comparar y reportar.",
      cap3Title: "Simplificación del archivo",
      cap3Text:
        "Reducimos pestañas innecesarias, cálculos duplicados, dependencias ocultas, pasos manuales y diseños confusos que dificultan el mantenimiento.",
      cap4Title: "Carga de datos más segura",
      cap4Text:
        "Agregamos tablas estructuradas, listas desplegables, reglas de validación, campos obligatorios, instrucciones y áreas protegidas para reducir errores evitables.",
      cap5Title: "Reportes y resúmenes",
      cap5Text:
        "Creamos vistas de resumen, filtros, reportes de estado, listas de atención y reportes recurrentes más claros a partir de la información del archivo.",
      cap6Title: "Automatización y migración",
      cap6Text:
        "Automatizamos tareas repetitivas o movemos el proceso a una base de datos, herramienta interna o flujo conectado cuando el archivo llegó a su límite práctico.",

      situationsEyebrow: "Situaciones comunes de rescate",
      situationsTitle: "El problema suele ser más grande que una fórmula rota",
      situation1Title: "Múltiples versiones del mismo archivo",
      situation1Text:
        "Los empleados guardan copias locales, envían archivos por correo o crean versiones separadas que ya no contienen la misma información.",
      situation2Title: "Fórmulas frágiles",
      situation2Text:
        "Los cálculos dependen de rangos fijos, fórmulas copiadas, pestañas ocultas o referencias que se rompen cuando alguien inserta una fila o cambia el nombre del archivo.",
      situation3Title: "Carga de datos inconsistente",
      situation3Text:
        "El mismo cliente, estado, servicio, fecha o ubicación se escribe de varias formas distintas, lo que vuelve poco confiables los filtros y reportes.",
      situation4Title: "Consolidación manual",
      situation4Text:
        "Alguien copia repetidamente información de varias hojas, sistemas, exportaciones o departamentos a un archivo maestro.",
      situation5Title: "Responsabilidad poco clara",
      situation5Text:
        "Los empleados no saben quién debe actualizar cada campo, cuál versión es la vigente o cuándo se revisó la información por última vez.",
      situation6Title: "Archivos lentos y sobredimensionados",
      situation6Text:
        "Fórmulas grandes, formatos repetidos, exceso de pestañas, vínculos externos o datos innecesarios dificultan abrir y usar el archivo.",
      situation7Title: "Reglas de negocio ocultas",
      situation7Text:
        "Reglas importantes de precios, aprobación, programación o clasificación existen solo dentro de fórmulas que pocas personas entienden.",
      situation8Title: "Reportes manuales",
      situation8Text:
        "El reporte semanal o mensual exige filtrar, copiar, formatear y recalcular la misma información una y otra vez.",
      situation9Title: "Control de acceso limitado",
      situation9Text:
        "Cualquiera con acceso al archivo puede ver o cambiar información más allá de lo que necesita para su rol.",
      situation10Title: "Dependencia del proceso",
      situation10Text:
        "Un flujo importante se detiene cuando la única persona que entiende la hoja de cálculo no está disponible.",
      situationsNote:
        "Un rescate de hoja de cálculo debe atender el riesgo operativo detrás de estos problemas—no solo dejar el archivo más prolijo.",

      exampleEyebrow: "Ejemplo de rescate",
      exampleTitle:
        "De un control de trabajos frágil a un archivo de operaciones más confiable",
      exampleIntro1:
        "Una empresa de servicios en crecimiento puede usar un solo archivo para registrar solicitudes de clientes, presupuestos, trabajos programados, empleados asignados, contratistas, estado de los trabajos, gastos, facturas y seguimientos.",
      exampleIntro2:
        "El archivo puede haber comenzado con unas pocas columnas, pero con el tiempo se convierte en el lugar central donde los empleados entienden qué está pasando. A medida que el negocio crece, el archivo se vuelve difícil de mantener y cada vez más riesgoso.",
      exampleDisclaimer:
        "Este es un ejemplo ilustrativo, no un caso real de un cliente.",
      exampleBeforeLabel: "Antes del rescate",
      exampleAfterLabel: "Después del rescate",
      before1Title: "Solicitudes y presupuestos",
      before1Text:
        "La información nueva se copia manualmente desde el correo, formularios u otra plataforma al archivo.",
      before2Title: "Registros inconsistentes",
      before2Text:
        "Cada empleado escribe de forma distinta los nombres de clientes, tipos de servicio, fechas y estados.",
      before3Title: "Seguimiento de asignaciones",
      before3Text:
        "Los empleados resaltan filas manualmente o agregan notas para indicar quién es responsable del trabajo.",
      before4Title: "Reportes rotos",
      before4Text:
        "Las fórmulas de resumen usan rangos fijos y no incluyen las filas agregadas después.",
      before5Title: "Múltiples versiones",
      before5Text:
        "Gerentes y empleados mantienen copias separadas por miedo a sobrescribir los cambios de otros.",
      before6Title: "Limpieza semanal",
      before6Text:
        "Alguien dedica varias horas a corregir registros y rehacer el reporte gerencial.",
      after1Title: "Registros estructurados",
      after1Text:
        "El archivo usa tablas organizadas con columnas, identificadores e información obligatoria consistentes.",
      after2Title: "Carga de datos validada",
      after2Text:
        "Listas desplegables, controles de fecha, opciones de estado e instrucciones reducen las entradas inconsistentes.",
      after3Title: "Responsabilidad clara",
      after3Text:
        "Las asignaciones, fechas de vencimiento y próximos pasos siguen una estructura consistente.",
      after4Title: "Resúmenes confiables",
      after4Text:
        "Los reportes y vistas de atención usan rangos estructurados y cálculos documentados.",
      after5Title: "Colaboración más segura",
      after5Text:
        "El archivo tiene responsabilidades más claras, cálculos protegidos y un proceso definido para actualizarlo.",
      after6Title: "Menos reportes manuales",
      after6Text:
        "Los resúmenes recurrentes y las listas de atención se actualizan desde los datos estructurados con menos preparación manual.",
      exampleFlowLabel: "El proceso rescatado",
      exampleFlow1: "Solicitud recibida",
      exampleFlow2: "Registro creado",
      exampleFlow3: "Presupuesto registrado",
      exampleFlow4: "Trabajo asignado",
      exampleFlow5: "Estado actualizado",
      exampleFlow6: "Seguimiento identificado",
      exampleFlow7: "Resumen semanal generado",
      exampleClosing:
        "La solución adecuada puede ser un archivo mejorado, un proceso de hoja de cálculo automatizado o una migración gradual hacia un sistema interno más estructurado.",

      levelEyebrow: "El nivel de rescate adecuado",
      levelTitle:
        "Algunas hojas de cálculo necesitan reparaciones. Otras ya superaron el formato.",
      levelText1: "Kranbos evalúa el proceso antes de recomendar una solución.",
      levelText2:
        "Una hoja de cálculo bien diseñada puede seguir siendo una herramienta efectiva para muchos negocios. Sin embargo, puede dejar de ser la mejor opción cuando demasiadas personas dependen de ella, el flujo requiere permisos complejos o el proceso necesita integrarse de forma confiable con varios sistemas.",
      levelWhenLabel: "Es adecuado cuando",
      levelImprovementsLabel: "Mejoras posibles",
      levelReplacementLabel: "Reemplazo posible",
      level1Title: "Reparar la hoja de cálculo",
      level1When1: "El archivo resuelve el problema correcto pero contiene errores",
      level1When2: "La cantidad de usuarios es manejable",
      level1When3: "El flujo de trabajo es relativamente simple",
      level1When4: "Mejores fórmulas, estructura y validación pueden mejorar la confiabilidad",
      level1When5: "La hoja de cálculo sigue siendo el formato más práctico",
      level1Item1: "Corrección de fórmulas",
      level1Item2: "Limpieza de datos",
      level1Item3: "Tablas estructuradas",
      level1Item4: "Diseño más claro",
      level1Item5: "Cálculos protegidos",
      level1Item6: "Validación y listas desplegables",
      level1Item7: "Documentación",
      level2Title: "Automatizar la hoja de cálculo",
      level2When1: "El archivo sigue siendo útil pero requiere actualizaciones manuales repetitivas",
      level2When2: "Hay que importar o exportar datos con regularidad",
      level2When3: "Los reportes se rehacen de forma recurrente",
      level2When4: "Los empleados copian información entre sistemas una y otra vez",
      level2When5: "Las notificaciones o seguimientos dependen del estado en la hoja",
      level2Item1: "Importaciones automáticas",
      level2Item2: "Exportaciones programadas",
      level2Item3: "Generación de reportes",
      level2Item4: "Notificaciones de estado",
      level2Item5: "Sincronización de registros",
      level2Item6: "Generación de documentos",
      level2Item7: "Disparadores de flujo",
      level3Title: "Reemplazar la hoja de cálculo",
      level3When1: "Muchos usuarios necesitan acceso simultáneo",
      level3When2: "El flujo requiere distintos roles y permisos",
      level3When3: "La hoja contiene demasiada lógica de negocio",
      level3When4: "Los registros necesitan mejor historial y auditoría",
      level3When5: "Los empleados necesitan formularios, aprobaciones, asignaciones o colas de trabajo",
      level3When6: "El archivo se volvió demasiado grande o frágil",
      level3When7: "El proceso necesita integraciones confiables con otros sistemas",
      level3Item1: "Herramienta interna de operaciones",
      level3Item2: "Aplicación con base de datos",
      level3Item3: "Portal de ingreso controlado",
      level3Item4: "Cola de trabajo",
      level3Item5: "Interfaz administrativa",
      level3Item6: "Sistema de reportes a medida",
      levelClosing:
        "Kranbos recomienda la solución práctica más pequeña que mejore la confiabilidad sin generar complejidad innecesaria.",

      cleanupEyebrow: "Información más limpia",
      cleanupTitle: "Las hojas de cálculo confiables dependen de datos consistentes",
      cleanupText1:
        "Un archivo puede tener fórmulas perfectas y aun así producir resultados engañosos si la información de base es inconsistente.",
      cleanupText2:
        "Kranbos ayuda a estandarizar los campos, categorías, estados, identificadores y reglas que se usan en todo el proceso.",
      cleanup1Title: "Categorías consistentes",
      cleanup1Text:
        "Estandarizamos servicios, departamentos, ubicaciones, tipos de cliente, estados y otros valores recurrentes.",
      cleanup2Title: "Detección de duplicados",
      cleanup2Text:
        "Identificamos clientes, proyectos, facturas, solicitudes u otros registros repetidos que pueden representar lo mismo.",
      cleanup3Title: "Información obligatoria",
      cleanup3Text:
        "Definimos qué campos deben completarse antes de que un registro avance o aparezca en un reporte.",
      cleanup4Title: "Definiciones documentadas",
      cleanup4Text:
        "Explicamos qué significan las etiquetas, estados, totales y cálculos importantes para que todos los usen igual.",
      cleanupNote:
        "La limpieza de datos debe revisarse con alguien que conozca el negocio. Kranbos no elimina ni combina registros dudosos de forma automática sin una regla aprobada o revisión humana.",

      collabEyebrow: "Colaboración más segura",
      collabTitle: "Que actualizar el archivo sea más fácil sin romperlo por accidente",
      collabText1:
        "Muchos problemas ocurren porque la carga de datos, los cálculos, los reportes y la configuración conviven en la misma área editable.",
      collabText2:
        "Kranbos puede reestructurar el archivo para que los empleados tengan lugares claros donde cargar información mientras las fórmulas y reglas importantes quedan protegidas.",
      collab1Title: "Cálculos protegidos",
      collab1Text:
        "Reducimos los cambios accidentales en fórmulas, resúmenes, tablas de referencia y otra lógica importante.",
      collab2Title: "Áreas de carga estructuradas",
      collab2Text:
        "Separamos la carga de datos de los cálculos y reportes para que los empleados sepan dónde hacer cambios.",
      collab3Title: "Responsabilidad clara",
      collab3Text:
        "Definimos quién es responsable de actualizar, revisar, corregir y preparar los reportes.",
      collab4Title: "Documentación de cambios",
      collab4Text:
        "Documentamos fórmulas, campos, pestañas, supuestos y pasos de mantenimiento para que el archivo dependa menos de una sola persona.",
      collabDisclaimer:
        "Las protecciones de una hoja de cálculo pueden reducir cambios accidentales, pero no reemplazan permisos adecuados de la plataforma, respaldos, controles de seguridad ni requisitos formales de cumplimiento.",

      autoEyebrow: "Menos copiar y pegar",
      autoTitle:
        "Deja la hoja de cálculo donde funciona. Automatiza los pasos a su alrededor.",
      autoText1:
        "Algunos negocios no necesitan reemplazar sus hojas de cálculo. Necesitan dejar de mover información hacia y desde ellas a mano.",
      autoText2:
        "Kranbos puede evaluar oportunidades para conectar el archivo con formularios, exportaciones, APIs aprobadas, procesos de correo, documentos, herramientas de reporte y otros sistemas del negocio.",
      auto1Title: "Importar registros nuevos",
      auto1Text:
        "Traer información aprobada desde formularios, plataformas, exportaciones u otras fuentes estructuradas hacia el archivo.",
      auto2Title: "Generar reportes recurrentes",
      auto2Text: "Preparar resúmenes semanales o mensuales sin rehacer el mismo reporte a mano.",
      auto3Title: "Crear documentos",
      auto3Text:
        "Usar datos aprobados de la hoja para completar presupuestos, resúmenes, cartas u otros documentos repetibles.",
      auto4Title: "Enviar notificaciones",
      auto4Text:
        "Avisar a la persona indicada cuando un registro está vencido, incompleto, sin asignar o listo para revisión.",
      auto5Title: "Actualizar sistemas conectados",
      auto5Text:
        "Enviar información aprobada de la hoja a otra plataforma cuando existe una integración confiable.",
      auto6Title: "Archivar registros completados",
      auto6Text:
        "Mover información antigua o completada a un archivo organizado sin perder historial importante.",
      autoNote:
        "Las opciones de automatización dependen de la plataforma de la hoja de cálculo, los sistemas de origen, los accesos disponibles y la confiabilidad de los datos.",
      autoCta: "Explorar Automatización de Flujos",

      reportingEyebrow: "Del archivo a la visibilidad",
      reportingTitle: "Convierte la hoja de cálculo en una vista operativa más clara",
      reportingText1:
        "Muchos negocios reúnen información útil en hojas de cálculo pero les cuesta convertirla en reportes fáciles de entender y mantener.",
      reportingText2:
        "Kranbos ayuda a organizar los datos detrás de resúmenes, vistas de estado, listas de atención y reportes gerenciales.",
      reportingCol1Title: "Mejora de la hoja de cálculo",
      reportingCol1Item1: "Limpiar y organizar los registros de origen",
      reportingCol1Item2: "Estandarizar estados y categorías",
      reportingCol1Item3: "Reparar cálculos",
      reportingCol1Item4: "Reducir datos duplicados",
      reportingCol1Item5: "Crear tablas estructuradas",
      reportingCol1Item6: "Mejorar filtros y validación",
      reportingCol2Title: "Tableros y Reportes",
      reportingCol2Item1: "Presentar resúmenes de alto nivel",
      reportingCol2Item2: "Seguir la actividad y la carga de trabajo",
      reportingCol2Item3: "Destacar trabajos vencidos o incompletos",
      reportingCol2Item4: "Comparar equipos, ubicaciones o períodos",
      reportingCol2Item5: "Entregar reportes gerenciales recurrentes",
      reportingCol2Item6: "Dar mejor soporte a las decisiones",
      reportingClosing:
        "En algunos casos, la hoja mejorada puede alimentar el reporte. En otros, el reporte debería moverse a un tablero o herramienta interna aparte.",
      reportingCta: "Explorar Tableros y Reportes",

      toolsEyebrow: "Más allá del archivo",
      toolsTitle:
        "Una hoja de cálculo no debería comportarse como una aplicación de negocio completa",
      toolsText1:
        "Un archivo puede volverse difícil de manejar cuando el proceso requiere muchos usuarios, distintos niveles de permisos, formularios, asignaciones, aprobaciones, comentarios, historial de actividad o integración con varias plataformas.",
      toolsText2:
        "En ese punto, una herramienta interna liviana puede ofrecer una experiencia operativa más clara y segura.",
      toolsCol1Title: "Una hoja de cálculo puede seguir siendo adecuada cuando",
      toolsCol1Item1: "El grupo de usuarios es pequeño",
      toolsCol1Item2: "El proceso es relativamente simple",
      toolsCol1Item3: "Los registros no requieren permisos complejos",
      toolsCol1Item4: "El archivo es fácil de entender para el equipo",
      toolsCol1Item5: "Las necesidades de colaboración son limitadas",
      toolsCol1Item6: "La hoja se puede mantener de forma confiable",
      toolsCol2Title: "Una herramienta interna puede ser más adecuada cuando",
      toolsCol2Item1: "Muchos empleados o contratistas necesitan acceso",
      toolsCol2Item2: "Distintos usuarios necesitan distintos permisos",
      toolsCol2Item3: "El equipo necesita una cola de trabajo compartida",
      toolsCol2Item4: "Los registros requieren asignaciones y aprobaciones",
      toolsCol2Item5: "El historial de actividad es importante",
      toolsCol2Item6: "Los empleados necesitan formularios estructurados",
      toolsCol2Item7: "El flujo conecta varios sistemas",
      toolsCol2Item8: "La hoja se volvió difícil de escalar de forma segura",
      toolsClosing:
        "Kranbos puede mejorar primero el archivo actual y crear un camino de migración cuando reemplazarlo de inmediato sería innecesariamente disruptivo.",
      toolsCta: "Explorar Herramientas Internas",

      processEyebrow: "Cómo funciona",
      processTitle: "Entender el archivo antes de cambiarlo",
      process1Title: "Revisión del archivo y del flujo",
      process1Text:
        "Revisamos la hoja de cálculo, sus usuarios, la información de origen, los reportes, las fórmulas, los pasos manuales y el proceso que sostiene.",
      process2Title: "Identificación de riesgos y problemas",
      process2Text:
        "Identificamos lógica rota, datos inconsistentes, trabajo duplicado, responsabilidades poco claras, problemas de rendimiento y áreas que dependen demasiado del esfuerzo manual.",
      process3Title: "Recomendación de rescate",
      process3Text:
        "Recomendamos si conviene reparar, reestructurar, automatizar, conectar o reemplazar gradualmente la hoja de cálculo.",
      process4Title: "Implementación y validación",
      process4Text:
        "Aplicamos las mejoras aprobadas, probamos cálculos y flujos, y revisamos las reglas de negocio dudosas con las personas indicadas.",
      process5Title: "Documentación y entrega",
      process5Text:
        "Documentamos la estructura del archivo, las fórmulas importantes, las expectativas de carga de datos, los pasos de mantenimiento y cualquier automatización o reporte nuevo.",
      processNote:
        "El trabajo necesario depende del tamaño y la complejidad del archivo, la documentación disponible, la calidad de los datos y la cantidad de archivos o sistemas conectados.",

      needEyebrow: "Antes del rescate",
      needTitle: "El archivo es solo una parte de la historia",
      needText1:
        "Para mejorar una hoja de cálculo de forma responsable, Kranbos necesita entender cómo la usa el negocio y en qué información se puede confiar.",
      need1: "El archivo actual y los archivos relacionados",
      need2: "Una descripción de para qué se usa la hoja de cálculo",
      need3: "Las personas que cargan o revisan la información",
      need4: "Errores conocidos o cálculos poco confiables",
      need5: "Ejemplos de reportes creados a partir del archivo",
      need6: "Archivos, exportaciones o sistemas de origen que proveen los datos",
      need7: "Definiciones de los campos, estados y totales importantes",
      need8: "Ejemplos del proceso manual actual",
      need9: "Versiones anteriores o alternativas del archivo",
      need10: "El resultado o la decisión que el archivo debería respaldar",
      needNote:
        "Los datos sensibles o de producción deben compartirse por un método aprobado. Cuando sea práctico, la revisión inicial puede usar una copia depurada que conserve la estructura del archivo sin exponer información confidencial innecesaria.",

      outcomesEyebrow: "El resultado",
      outcomesTitle: "Una hoja de cálculo que tu equipo entiende, mantiene y puede usar con confianza",
      outcome1Title: "Cálculos más confiables",
      outcome1Text:
        "Menos fórmulas rotas, referencias poco claras, lógica inconsistente y errores en los reportes.",
      outcome2Title: "Información más limpia",
      outcome2Text:
        "Registros más fáciles de cargar, filtrar, comparar, revisar y reportar.",
      outcome3Title: "Menos trabajo manual",
      outcome3Text:
        "Menos copiado, formateo, consolidación y preparación de reportes repetitivos.",
      outcome4Title: "Un camino más claro",
      outcome4Text:
        "Saber si el proceso debe seguir en una hoja de cálculo, automatizarse o moverse a una herramienta más estructurada.",

      relatedTitle: "La hoja de cálculo puede ser parte de un problema operativo mayor",
      relatedIntro:
        "Kranbos puede combinar mejoras de hojas de cálculo, automatización, herramientas internas y reportes en una sola solución práctica.",
      relatedCurrentLabel: "Estás aquí",
      related1Title: "Rescate de Hojas de Cálculo",
      related1Text:
        "Reparar, simplificar, automatizar o reemplazar un proceso basado en hojas de cálculo.",
      related2Title: "Automatización de Flujos",
      related2Text:
        "Conectar sistemas y reducir el movimiento repetitivo de información y acciones.",
      related3Title: "Herramientas Internas",
      related3Text:
        "Reemplazar una hoja de cálculo sobrecargada por una interfaz operativa enfocada cuando sea necesario.",
      related4Title: "Tableros y Reportes",
      related4Text:
        "Convertir información organizada en reportes, resúmenes y vistas de atención más claros.",
      relatedLearnMore: "Conocer más",

      finalCtaTitle: "Muéstranos la hoja de cálculo que nadie se anima a tocar",
      finalCtaText:
        "Cuéntanos qué hace el archivo, dónde está causando problemas y qué necesita tu equipo de él. Te ayudamos a determinar si conviene repararlo, automatizarlo o reemplazarlo.",
      finalPrimaryCta: "Solicitar una Revisión de Hoja de Cálculo",
      finalSecondaryCta: "Contáctanos",
    },
  },
};
